import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { analyticsApi, reservationsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, Euro, Calendar, Moon, Users, Target, Sparkles, Clock, LogIn, LogOut as LogOutIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import AnimatedCounter from '../components/AnimatedCounter';
import { fireSuccessConfetti } from '../components/Confetti';
import { format, parseISO, isToday, isTomorrow } from 'date-fns';
import { fr } from 'date-fns/locale';

const COLORS = ['#ec4899', '#3b82f6', '#c9a227', '#10b981', '#8b5cf6'];

export default function DashboardPage() {
  const { selectedProperty } = useOutletContext();
  const [summary, setSummary] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthlyGoal, setMonthlyGoal] = useState(1000); // Default goal
  const [showWelcome, setShowWelcome] = useState(false);

  useEffect(() => {
    loadData();
    // Show welcome animation on first load
    const hasSeenWelcome = sessionStorage.getItem('hasSeenWelcome');
    if (!hasSeenWelcome) {
      setShowWelcome(true);
      sessionStorage.setItem('hasSeenWelcome', 'true');
      setTimeout(() => fireSuccessConfetti(), 500);
    }
  }, [selectedProperty]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = selectedProperty !== 'all' ? { property_id: selectedProperty } : {};
      const [summaryRes, reservationsRes] = await Promise.all([
        analyticsApi.getSummary(params),
        reservationsApi.getAll(params),
      ]);
      setSummary(summaryRes.data);
      setReservations(reservationsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  // Get today's check-ins and check-outs
  const getTodayActivity = () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const tomorrow = format(new Date(Date.now() + 86400000), 'yyyy-MM-dd');
    
    const checkInsToday = reservations.filter(r => r.check_in === today);
    const checkOutsToday = reservations.filter(r => r.check_out === today);
    const checkInsTomorrow = reservations.filter(r => r.check_in === tomorrow);
    
    return { checkInsToday, checkOutsToday, checkInsTomorrow };
  };

  // Calculate occupancy rate
  const getOccupancyRate = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    const daysInMonth = endOfMonth.getDate();
    
    let occupiedDays = 0;
    reservations.forEach(r => {
      const checkIn = new Date(r.check_in);
      const checkOut = new Date(r.check_out);
      
      // Count days that overlap with current month
      const overlapStart = Math.max(checkIn.getTime(), startOfMonth.getTime());
      const overlapEnd = Math.min(checkOut.getTime(), endOfMonth.getTime());
      
      if (overlapEnd > overlapStart) {
        occupiedDays += Math.ceil((overlapEnd - overlapStart) / 86400000);
      }
    });
    
    return Math.min(100, Math.round((occupiedDays / daysInMonth) * 100));
  };

  // Process data for charts
  const getMonthlyData = () => {
    const monthlyRevenue = {};
    reservations.forEach(r => {
      const month = r.check_in?.substring(0, 7);
      if (month) {
        if (!monthlyRevenue[month]) {
          monthlyRevenue[month] = { month, revenue: 0, reservations: 0 };
        }
        monthlyRevenue[month].revenue += r.total_price || r.net_revenue || 0;
        monthlyRevenue[month].reservations += 1;
      }
    });
    return Object.values(monthlyRevenue)
      .sort((a, b) => a.month.localeCompare(b.month))
      .slice(-6)
      .map(d => ({
        ...d,
        name: new Date(d.month + '-01').toLocaleDateString('fr-FR', { month: 'short' })
      }));
  };

  const getPlatformData = () => {
    const platforms = {};
    reservations.forEach(r => {
      const platform = r.platform || 'direct';
      if (!platforms[platform]) {
        platforms[platform] = { name: platform, value: 0, count: 0 };
      }
      platforms[platform].value += r.total_price || r.net_revenue || 0;
      platforms[platform].count += 1;
    });
    return Object.values(platforms);
  };

  const monthlyData = getMonthlyData();
  const platformData = getPlatformData();
  const { checkInsToday, checkOutsToday, checkInsTomorrow } = getTodayActivity();
  const occupancyRate = getOccupancyRate();
  const monthRevenue = summary?.month_revenue || 0;
  const goalProgress = Math.min(100, (monthRevenue / monthlyGoal) * 100);

  if (loading) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
        <div className="flex items-center gap-3">
          <Sparkles className="h-8 w-8 text-primary animate-pulse" />
          <h1 className="text-2xl md:text-3xl font-heading font-semibold">Chargement...</h1>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="bg-card border-border/40 animate-pulse">
              <CardContent className="pt-4"><div className="h-4 bg-muted rounded w-1/2 mb-2" /><div className="h-6 bg-muted rounded w-3/4" /></CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Header with greeting */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-semibold tracking-tight flex items-center gap-3">
            {showWelcome && <Sparkles className="h-7 w-7 text-primary animate-bounce" />}
            Bienvenue sur Era Lodge
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            {format(new Date(), "EEEE d MMMM yyyy", { locale: fr })}
          </p>
        </div>
        
        {/* Quick stats pills */}
        <div className="flex flex-wrap gap-2">
          {checkInsToday.length > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/20 text-green-400 rounded-full text-xs font-medium animate-pulse">
              <LogIn className="h-3 w-3" />
              {checkInsToday.length} arrivée(s) aujourd'hui
            </div>
          )}
          {checkOutsToday.length > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-500/20 text-orange-400 rounded-full text-xs font-medium">
              <LogOutIcon className="h-3 w-3" />
              {checkOutsToday.length} départ(s) aujourd'hui
            </div>
          )}
        </div>
      </div>

      {/* Today's Activity Card */}
      {(checkInsToday.length > 0 || checkOutsToday.length > 0 || checkInsTomorrow.length > 0) && (
        <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="h-5 w-5 text-primary" />
              <span className="font-medium">Activité du jour</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {checkInsToday.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-green-400 font-medium uppercase">Arrivées aujourd'hui</p>
                  {checkInsToday.map(r => (
                    <div key={r.id} className="text-sm">
                      <span className="font-medium">{r.guest_name || 'Invité'}</span>
                      {r.total_price > 0 && <span className="text-muted-foreground"> · €{r.total_price.toFixed(2)}</span>}
                    </div>
                  ))}
                </div>
              )}
              {checkOutsToday.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-orange-400 font-medium uppercase">Départs aujourd'hui</p>
                  {checkOutsToday.map(r => (
                    <div key={r.id} className="text-sm">
                      <span className="font-medium">{r.guest_name || 'Invité'}</span>
                    </div>
                  ))}
                </div>
              )}
              {checkInsTomorrow.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-blue-400 font-medium uppercase">Arrivées demain</p>
                  {checkInsTomorrow.map(r => (
                    <div key={r.id} className="text-sm">
                      <span className="font-medium">{r.guest_name || 'Invité'}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Key Stats with animated counters */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border/40 hover:border-primary/50 transition-colors">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Revenus totaux</span>
              <Euro className="h-4 w-4 text-primary" />
            </div>
            <p className="text-2xl font-semibold text-primary">
              <AnimatedCounter value={summary?.total_revenue || 0} prefix="€" decimals={2} />
            </p>
          </CardContent>
        </Card>
        
        <Card className="bg-card border-border/40 hover:border-green-500/50 transition-colors">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Ce mois</span>
              <TrendingUp className="h-4 w-4 text-green-400" />
            </div>
            <p className="text-2xl font-semibold text-green-400">
              <AnimatedCounter value={monthRevenue} prefix="€" decimals={2} />
            </p>
          </CardContent>
        </Card>
        
        <Card className="bg-card border-border/40 hover:border-blue-500/50 transition-colors">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Réservations</span>
              <Users className="h-4 w-4 text-blue-400" />
            </div>
            <p className="text-2xl font-semibold">
              <AnimatedCounter value={summary?.total_reservations || reservations.length} />
            </p>
          </CardContent>
        </Card>
        
        <Card className="bg-card border-border/40 hover:border-purple-500/50 transition-colors">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-muted-foreground uppercase tracking-wider">Taux occupation</span>
              <Moon className="h-4 w-4 text-purple-400" />
            </div>
            <p className="text-2xl font-semibold text-purple-400">
              <AnimatedCounter value={occupancyRate} suffix="%" />
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Goal Progress */}
      <Card className="bg-card border-border/40">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              <span className="font-medium">Objectif mensuel</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">€{monthRevenue.toFixed(0)} / €{monthlyGoal}</span>
              <span className={`text-sm font-medium ${goalProgress >= 100 ? 'text-green-400' : 'text-primary'}`}>
                {goalProgress.toFixed(0)}%
              </span>
            </div>
          </div>
          <Progress 
            value={goalProgress} 
            className="h-3"
            indicatorClassName={goalProgress >= 100 ? 'bg-green-500' : 'bg-primary'}
          />
          {goalProgress >= 100 && (
            <p className="text-xs text-green-400 mt-2 flex items-center gap-1">
              <Sparkles className="h-3 w-3" />
              Félicitations ! Objectif atteint !
            </p>
          )}
        </CardContent>
      </Card>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <Card className="bg-card border-border/40">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base">Revenus par mois</CardTitle>
          </CardHeader>
          <CardContent>
            {monthlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={monthlyData}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#c9a227" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#c9a227" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="name" tick={{ fill: '#888', fontSize: 12 }} />
                  <YAxis tick={{ fill: '#888', fontSize: 12 }} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                    labelStyle={{ color: '#fff' }}
                    formatter={(value) => [`€${value.toFixed(2)}`, 'Revenus']}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="#c9a227" strokeWidth={2} fillOpacity={1} fill="url(#colorRevenue)" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-muted-foreground">
                Aucune donnée disponible
              </div>
            )}
          </CardContent>
        </Card>

        {/* Platform Distribution */}
        <Card className="bg-card border-border/40">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-base">Répartition par plateforme</CardTitle>
          </CardHeader>
          <CardContent>
            {platformData.length > 0 ? (
              <div className="flex flex-col md:flex-row items-center">
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={platformData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {platformData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                      formatter={(value) => [`€${value.toFixed(2)}`, 'Revenus']}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap justify-center gap-3 mt-2">
                  {platformData.map((entry, index) => (
                    <div key={entry.name} className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                      <span className="text-xs capitalize">{entry.name}</span>
                      <span className="text-xs text-muted-foreground">({entry.count})</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-muted-foreground">
                Aucune donnée disponible
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Reservations */}
      <Card className="bg-card border-border/40">
        <CardHeader>
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            Dernières réservations
          </CardTitle>
        </CardHeader>
        <CardContent>
          {reservations.length === 0 ? (
            <p className="text-center text-muted-foreground py-6">Aucune réservation</p>
          ) : (
            <div className="space-y-2">
              {reservations.slice(0, 8).map((res, index) => (
                <div 
                  key={res.id} 
                  className="flex items-center justify-between p-3 bg-muted/30 rounded-sm hover:bg-muted/50 transition-all hover:translate-x-1"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{res.guest_name || 'Invité'}</p>
                    <p className="text-xs text-muted-foreground">{res.check_in} → {res.check_out} · {res.nights || 1} nuit(s)</p>
                  </div>
                  <div className="text-right ml-4">
                    <p className="font-semibold text-primary tabular-nums">€{(res.total_price || res.net_revenue || 0).toFixed(2)}</p>
                    <p className="text-xs text-muted-foreground capitalize">{res.platform || 'direct'}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
